import { Box, Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { differenceInDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { currentContextAtom } from '../../state';
import suggestionIcon from '../../assets/images/naming-wizard-suggestion.png';
import heroImage from '../../assets/images/naming-optimization-hero.png';
import { floatingCardEntranceSx } from '../../common/components/floatingCardEntrance';
import useProductListCounts from './useProductListCounts';

interface ProductNameOptimizationFloatingCardProps {
  onAction: () => void;
}

const DISMISS_DURATION_IN_DAYS = 7;

export default function ProductNameOptimizationFloatingCard({ onAction }: ProductNameOptimizationFloatingCardProps): React.JSX.Element | null {
  const { t } = useTranslation(['products']);
  const { companyId } = useAtomValue(currentContextAtom);
  const dismissedAtKey = `${companyId}-product-name-optimization-card-dismissed-at`;
  const [isDismissed, setIsDismissed] = useState(() => differenceInDays(new Date(), Number(localStorage.getItem(dismissedAtKey))) < DISMISS_DURATION_IN_DAYS);
  const { namesToOptimizeCount } = useProductListCounts();

  const handleDismiss = (): void => {
    localStorage.setItem(dismissedAtKey, Date.now().toString());
    setIsDismissed(true);
  };

  if (isDismissed || !namesToOptimizeCount) {
    return null;
  }

  return (
    <Box
      sx={{
        ...floatingCardEntranceSx,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 1.5,
        position: 'fixed',
        bottom: { xs: 8, sm: 12, lg: 16 },
        right: { xs: 8, sm: 12, lg: 16 },
        zIndex: 1002,
      }}
    >
      <Button variant="tonal" onClick={handleDismiss}>
        <CloseIcon />
      </Button>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column-reverse', lg: 'row' },
          alignItems: { xs: 'stretch', lg: 'center' },
          gap: 1.5,
          width: { xs: 308, lg: 640 },
          pl: { xs: 1, lg: 2 },
          pr: 1,
          py: 1,
          borderRadius: 4,
          backgroundColor: 'primaryContainer.main',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box component="img" src={suggestionIcon} alt="" sx={{ width: 24, height: 24 }} />
              <Typography variant="titleMedium" color="onPrimaryContainer">
                {t('products:name_optimization.floating_card_title')}
              </Typography>
            </Box>
            <Typography variant="bodySmall" color="onPrimaryContainer">
              {t('products:name_optimization.floating_card_description')}
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<AutoAwesomeOutlinedIcon />} onClick={onAction} sx={{ height: 40, minHeight: 40 }}>
            {t('products:name_optimization.floating_card_action')}
          </Button>
        </Box>
        <Box
          component="img"
          src={heroImage}
          alt=""
          sx={{
            width: { xs: '100%', lg: 200 },
            height: { xs: 'auto', lg: 150 },
            aspectRatio: { xs: '3/1', lg: 'auto' },
            objectFit: 'cover',
            borderRadius: 2,
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );
}
